/* eslint-disable @typescript-eslint/no-unused-vars */
import { TRIVIA_FETCH, TRIVIA_LIST, TRIVIA_SET_INTEREST, TRIVIA_VALIDATE } from 'constants/api'
import axios from 'axios'
import React, { Dispatch, FC, useContext, useReducer, useEffect } from 'react'
import equals from 'lodash.isequal'
import { useHistory } from 'react-router-dom'
import textServices from 'services/text-services'
import { useSession } from '../session-context'
import reducer, { Trivia, TriviaAction, TriviaActionsType, TriviaState } from './reducer'

export interface TriviaReducer {
  state: TriviaState
  dispatch: Dispatch<TriviaAction>
}

const TriviaContext = React.createContext<TriviaReducer>({} as TriviaReducer)

const TriviaProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    interests: []
  } as TriviaState)

  return <TriviaContext.Provider value={{ state, dispatch }}>{children}</TriviaContext.Provider>
}

export const useTrivia = () => {
  const history = useHistory()

  const [{ data: session }] = useSession()
  const [{ user }, { setUser, setPoints }] = useSession()

  const { state, dispatch } = useContext(TriviaContext)

  useEffect(() => {
    if (user) {
      const mappedInterests = user?.interests.map(({ id }) => id)
      const contentChanged = !equals(mappedInterests, state.interests)

      if (contentChanged) {
        dispatch({
          type: TriviaActionsType.SET_INTERESTS,
          payload: mappedInterests
        })
      }
    }
  }, [user])

  const setInterests = async (interests: TriviaState['interests']) => {
    try {
      const response = await axios.put(TRIVIA_SET_INTEREST, interests, {
        headers: { Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}` }
      })

      setUser(response.data)

      dispatch({
        type: TriviaActionsType.SET_INTERESTS,
        payload: interests
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getTrivias = async () => {
    const response = await axios.get(TRIVIA_LIST, {
      headers: { Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}` }
    })
    dispatch({
      type: TriviaActionsType.SET_TRIVIAS,
      payload: response.data
    })
    return response.data
  }

  const getTrivia = async (id: number | string) => {
    const response = await axios.post<Trivia>(`${TRIVIA_FETCH}/${id}`, undefined, {
      headers: { Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}` }
    })

    const currentPoints = -response.data.costInPoints

    setPoints(currentPoints)

    dispatch({
      type: TriviaActionsType.SET_TRIVIA,
      payload: response.data
    })
  }

  const validateTrivia = async (triviaId: number | string, questionId: number | string, optionId: number | string) => {
    try {
      const response = await axios.put(
        TRIVIA_VALIDATE,
        { triviaId, questionId, optionId },
        {
          headers: { Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}` }
        }
      )
      // console.log('===========================================', response.data)
      if (response.data.question) {
        dispatch({
          type: TriviaActionsType.SET_QUESTIONS,
          payload: response.data.question
        })

        return true
      }

      if (response.data.prize) {
        setPoints(response.data.prize.points)

        dispatch({
          type: TriviaActionsType.SET_PRIZE,
          payload: response.data.prize
        })

        return false
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const resetTrivia = () => {
    // setPoints(points)

    dispatch({
      type: TriviaActionsType.RESET,
      payload: null
    })
  }

  const setError = (err: any) => {
    dispatch({
      type: TriviaActionsType.SET_ERROR,
      payload: err
    })
  }

  const exchangePrize = (points: number) => {
    history.push(textServices.findByText('trivias/exchange/button/url', '/profile/history'))
  }

  const methods = { setInterests, getTrivias, getTrivia, validateTrivia, resetTrivia, exchangePrize, setError }

  const values = [state, methods] as [TriviaState, typeof methods]

  return values
}

export default TriviaProvider
